/**
 * Document Synchronization Manager & Conflict Resolution (§6.1, §7, SAAS-411).
 *
 * Implements:
 * - Local draft auto-save via DraftStore (IndexedDB / memory fallback).
 * - Remote Compare-And-Swap (CAS) revision saving with If-Match / ETag.
 * - HTTP 409 conflict detection without data loss.
 * - Conflict resolution:
 *     1. Fork into explicit conflict branch (remote detached revision + local conflict branch).
 *     2. Standalone JSON draft backup download.
 *     3. Discard local edits and pull latest remote head.
 *     4. Force promote local head over remote head.
 * - Offline detection and seamless offline-to-online recovery.
 * - Session expiration (HTTP 401) draft protection.
 */

import { Paper2InkApiClient, ApiClientError } from "../api/client.ts";
import type { RevisionResponse, DocumentResponse } from "../api/types.ts";
import { DraftStore } from "../storage/draftStore.ts";
import { BranchWorkflowManager, type BranchKind, type DocumentBranch } from "./branchWorkflow.ts";
import { type CIMDocument, sanitizeCIMDocument } from "./model.ts";
import { exportToJSON } from "../export/jsonExport.ts";

export type SyncState = "clean" | "dirty" | "saving" | "conflict" | "offline" | "error";

export interface ConflictInfo {
  documentId: string;
  localRevisionId: string | null;
  remoteHeadRevisionId: string | null;
  conflictedAt: number;
  localDocument: CIMDocument;
  message: string;
}

export type SyncStateListener = (state: SyncState, conflict: ConflictInfo | null) => void;

export interface DocumentSyncOptions {
  api: Paper2InkApiClient;
  draftStore: DraftStore;
  workflow: BranchWorkflowManager;
  documentId: string;
  initialHeadRevisionId?: string | null;
  autoSaveDebounceMs?: number;
  onStateChange?: SyncStateListener;
  onStatusMessage?: (msg: string) => void;
  onSessionExpired?: (doc: CIMDocument, fallbackJson: string) => void;
}

export class DocumentSyncManager {
  private readonly api: Paper2InkApiClient;
  private readonly draftStore: DraftStore;
  private workflow: BranchWorkflowManager;
  private documentId: string;
  private currentHeadRevisionId: string | null = null;
  private autoSaveDebounceMs: number;

  private state: SyncState = "clean";
  private conflictInfo: ConflictInfo | null = null;
  private listeners: Set<SyncStateListener> = new Set();
  private debounceTimer: any = null;
  private isDestroyed = false;

  private onStatusMessage?: (msg: string) => void;
  private onSessionExpired?: (doc: CIMDocument, fallbackJson: string) => void;

  private boundOnlineHandler?: () => void;
  private boundOfflineHandler?: () => void;

  constructor(options: DocumentSyncOptions) {
    this.api = options.api;
    this.draftStore = options.draftStore;
    this.workflow = options.workflow;
    this.documentId = options.documentId;
    this.currentHeadRevisionId = options.initialHeadRevisionId || null;
    this.autoSaveDebounceMs = options.autoSaveDebounceMs ?? 1500;
    this.onStatusMessage = options.onStatusMessage;
    this.onSessionExpired = options.onSessionExpired;

    if (options.onStateChange) {
      this.listeners.add(options.onStateChange);
    }

    this.setupNetworkListeners();
  }

  public getSyncState(): SyncState {
    return this.state;
  }

  public getConflictInfo(): ConflictInfo | null {
    return this.conflictInfo;
  }

  public getDocumentId(): string {
    return this.documentId;
  }

  public setDocumentId(id: string): void {
    this.documentId = id;
  }

  public getCurrentHeadRevisionId(): string | null {
    return this.currentHeadRevisionId;
  }

  public setCurrentHeadRevisionId(revId: string | null): void {
    this.currentHeadRevisionId = revId;
  }

  public setWorkflow(workflow: BranchWorkflowManager): void {
    this.workflow = workflow;
  }

  public getWorkflow(): BranchWorkflowManager {
    return this.workflow;
  }

  public onStateChange(listener: SyncStateListener): () => void {
    this.listeners.add(listener);
    listener(this.state, this.conflictInfo);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyState(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.state, this.conflictInfo);
      } catch (err) {
        console.error("Error in SyncStateListener:", err);
      }
    }
  }

  private setupNetworkListeners(): void {
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      this.boundOnlineHandler = () => {
        if (this.state === "offline") {
          this.state = this.workflow.hasManualEdits() ? "dirty" : "clean";
          this.onStatusMessage?.("İnternet bağlantısı yeniden kuruldu.");
          this.notifyState();
        }
      };

      this.boundOfflineHandler = () => {
        this.state = "offline";
        this.onStatusMessage?.("Çevrimdışı moda geçildi. Değişiklikler yerel hafızada (IndexedDB) saklanıyor.");
        this.notifyState();
      };

      window.addEventListener("online", this.boundOnlineHandler);
      window.addEventListener("offline", this.boundOfflineHandler);

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        this.state = "offline";
      }
    }
  }

  /**
   * Invoked whenever local document is modified.
   * Immediately persists draft to local IndexedDB and queues a debounced cloud save.
   */
  public async markDirty(options: { autoSaveCloud?: boolean } = {}): Promise<void> {
    if (this.isDestroyed) return;

    this.workflow.markDirty();
    const doc = this.workflow.getActiveDocument();

    // 1. Immediately persist local draft to prevent crash data loss (§10.2, SAAS-217)
    try {
      await this.draftStore.saveDraft(doc, {
        branchId: this.workflow.activeBranch.id,
      });
    } catch (storeErr) {
      console.warn("Local draft auto-save warning:", storeErr);
    }

    if (this.state !== "conflict") {
      this.state = "dirty";
      this.notifyState();
    }

    // 2. Debounce cloud save if enabled
    if (options.autoSaveCloud && (this.state as SyncState) === "dirty") {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        if (!this.isDestroyed && this.state === "dirty") {
          this.saveToCloud().catch((err) => {
            console.warn("Debounced cloud auto-save skipped:", err.message);
          });
        }
      }, this.autoSaveDebounceMs);

      if (typeof this.debounceTimer?.unref === "function") {
        this.debounceTimer.unref();
      }
    }
  }

  /**
   * Save local changes to the cloud repository using optimistic CAS locking (§6.1, SAAS-309).
   */
  public async saveToCloud(options: {
    mode?: "advance_head" | "detached";
    branchKind?: BranchKind;
    branchId?: string;
  } = {}): Promise<RevisionResponse> {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    const doc = this.workflow.getActiveDocument();
    const mode = options.mode || "advance_head";
    const branchKind = options.branchKind || "manual";
    const validBranchKind = (branchKind === "main" ? undefined : branchKind) as
      | "conflict"
      | "conversion"
      | "mask_edit"
      | "manual"
      | undefined;

    this.state = "saving";
    this.notifyState();
    this.onStatusMessage?.("Belge buluta kaydediliyor...");

    try {
      const rev = await this.api.documents.createRevision(
        this.documentId,
        {
          content: sanitizeCIMDocument(doc) as unknown as Record<string, unknown>,
          expected_head_revision_id: this.currentHeadRevisionId,
          mode,
          branch_kind: validBranchKind,
        },
        this.currentHeadRevisionId || undefined
      );

      // Save succeeded: update pointers
      this.currentHeadRevisionId = rev.id;
      this.workflow.addRevision(
        this.workflow.activeBranch.id,
        rev.id,
        doc,
        `Cloud revision ${rev.id.substring(0, 8)}`
      );

      this.state = "clean";
      this.conflictInfo = null;
      this.notifyState();
      this.onStatusMessage?.(`Değişiklikler buluta kaydedildi (Revizyon: ${rev.id.substring(0, 8)}).`);

      // Update local draft metadata
      await this.draftStore.saveDraft(doc, {
        branchId: this.workflow.activeBranch.id,
      }).catch(() => {});

      return rev;
    } catch (err: any) {
      if (err instanceof ApiClientError) {
        if (err.status === 409) {
          // HTTP 409 Conflict: Remote head has changed (§6.1, SAAS-411)
          const remoteHead =
            typeof err.details?.current_head_revision_id === "string"
              ? err.details.current_head_revision_id
              : typeof err.details?.remote_head_revision_id === "string"
              ? err.details.remote_head_revision_id
              : null;

          this.conflictInfo = {
            documentId: this.documentId,
            localRevisionId: this.currentHeadRevisionId,
            remoteHeadRevisionId: remoteHead,
            conflictedAt: Date.now(),
            localDocument: structuredClone(doc),
            message: err.message || "Belge sunucuda başka bir sekme veya oturum tarafından güncellenmiş.",
          };

          this.state = "conflict";
          this.notifyState();
          this.onStatusMessage?.("Kaydetme çatışması (409): Belge sunucuda güncellenmiş. Değişiklikleriniz korundu.");

          // Crucial: preserve conflicting local draft in DraftStore
          await this.draftStore.saveDraft(doc, {
            branchId: this.workflow.activeBranch.id,
          }).catch(() => {});

          throw err;
        }

        if (err.status === 401) {
          // HTTP 401 Session expired: preserve local draft
          const fallbackJson = exportToJSON(doc);
          await this.draftStore.saveDraft(doc, {
            branchId: this.workflow.activeBranch.id,
          }).catch(() => {});

          this.state = "error";
          this.notifyState();
          this.onSessionExpired?.(doc, fallbackJson);
          throw err;
        }
      }

      // Check if network is offline
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        this.state = "offline";
        this.notifyState();
        this.onStatusMessage?.("Ağ bağlantısı yok. Taslak çevrimdışı hafızada korundu.");
        throw err;
      }

      this.state = "error";
      this.notifyState();
      this.onStatusMessage?.(`Kaydetme hatası: ${err.message}`);
      throw err;
    }
  }

  /**
   * Conflict Resolution 1: Fork into an explicit conflict branch (§6.1, SAAS-411).
   * Creates a dedicated conflict branch on the server (mode: "detached", branch_kind: "conflict"),
   * branches locally in BranchWorkflowManager, and switches to it.
   */
  public async forkConflictBranch(customName?: string): Promise<{
    branchId: string;
    revisionId: string;
    branch: DocumentBranch;
  }> {
    const doc = this.workflow.getActiveDocument();
    const branchName = customName || `conflict_${Date.now().toString(36).substring(2, 7)}`;

    this.onStatusMessage?.("Çatışma dalı oluşturuluyor...");

    // 1. Create detached revision on server with branch_kind: 'conflict'
    const rev = await this.api.documents.createRevision(
      this.documentId,
      {
        content: sanitizeCIMDocument(doc) as unknown as Record<string, unknown>,
        parent_revision_id: this.conflictInfo?.remoteHeadRevisionId || this.currentHeadRevisionId,
        mode: "detached",
        branch_kind: "conflict",
      }
    );

    // 2. Create branch on server pointing to this revision
    let remoteBranchId: string | undefined;
    try {
      const branchRes = await this.api.documents.createBranch(this.documentId, {
        base_revision_id: this.conflictInfo?.remoteHeadRevisionId || rev.id,
        revision_id: rev.id,
        kind: "conflict",
      });
      remoteBranchId = branchRes.id;
    } catch (branchErr) {
      console.warn("Could not create explicit branch entity on server:", branchErr);
    }

    // 3. Create conflict branch in local workflow and switch to it
    const localConflict = this.workflow.createConflictBranch(branchName, rev.id);

    this.currentHeadRevisionId = rev.id;
    this.conflictInfo = null;
    this.state = "clean";
    this.notifyState();
    this.onStatusMessage?.(`Çatışma çözüldü: '${localConflict.branch.name}' adlı yeni dala geçildi.`);

    await this.draftStore.saveDraft(this.workflow.getActiveDocument(), {
      branchId: localConflict.branch.id,
    }).catch(() => {});

    return {
      branchId: localConflict.branch.id,
      revisionId: rev.id,
      branch: localConflict.branch,
    };
  }

  /**
   * Conflict Resolution 2: Standalone JSON draft backup download (§6.1, SAAS-411).
   * Ensures the user can immediately extract their work as a portable .paper2ink.json file.
   */
  public downloadConflictDraft(): { jsonString: string; filename: string } {
    const doc = this.conflictInfo?.localDocument || this.workflow.getActiveDocument();
    const jsonString = exportToJSON(doc);
    const filename = `paper2ink_${this.documentId}_conflict_backup_${Date.now()}.json`;

    if (typeof document !== "undefined" && typeof document.createElement === "function") {
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    this.onStatusMessage?.("Çatışan taslak JSON dosyası olarak indirildi.");
    return { jsonString, filename };
  }

  /**
   * Conflict Resolution 3: Discard local edits and pull latest server head (§6.1, SAAS-411).
   */
  public async discardLocalAndPull(): Promise<CIMDocument> {
    this.onStatusMessage?.("Sunucudaki güncel belge çekiliyor...");

    const remoteHeadRevId =
      this.conflictInfo?.remoteHeadRevisionId ||
      (await this.api.documents.get(this.documentId)).head_revision_id;

    if (!remoteHeadRevId) {
      throw new Error("No remote head revision found on server");
    }

    const rev = await this.api.documents.getRevision(this.documentId, remoteHeadRevId);
    const serverDoc = rev.cim_document as unknown as CIMDocument;
    if (!serverDoc) {
      throw new Error("Remote revision does not contain valid CIM document");
    }

    this.workflow.replaceActiveDocument(serverDoc, rev.id);
    this.currentHeadRevisionId = rev.id;
    this.conflictInfo = null;
    this.state = "clean";
    this.notifyState();
    this.onStatusMessage?.(`Sunucudaki güncel revizyon (${rev.id.substring(0, 8)}) uygulandı.`);

    await this.draftStore.saveDraft(serverDoc, {
      branchId: this.workflow.activeBranch.id,
    }).catch(() => {});

    return serverDoc;
  }

  /**
   * Conflict Resolution 4: Force overwrite remote head with local document (§6.1, SAAS-411).
   */
  public async forceOverwriteRemote(): Promise<RevisionResponse> {
    this.onStatusMessage?.("Değişiklikler sunucu üzerine zorla yazılıyor...");

    // Fetch latest remote head to pass as valid expected_head_revision_id
    const remoteDoc = await this.api.documents.get(this.documentId);
    const remoteHeadId = remoteDoc.head_revision_id;

    const doc = this.workflow.getActiveDocument();
    const rev = await this.api.documents.createRevision(
      this.documentId,
      {
        content: sanitizeCIMDocument(doc) as unknown as Record<string, unknown>,
        expected_head_revision_id: remoteHeadId,
        mode: "advance_head",
        branch_kind: "manual",
      },
      remoteHeadId || undefined
    );

    this.currentHeadRevisionId = rev.id;
    this.workflow.addRevision(
      this.workflow.activeBranch.id,
      rev.id,
      doc,
      `Force overwrite revision ${rev.id.substring(0, 8)}`
    );

    this.conflictInfo = null;
    this.state = "clean";
    this.notifyState();
    this.onStatusMessage?.(`Değişiklikler sunucuya yazıldı (Yeni Baş: ${rev.id.substring(0, 8)}).`);

    return rev;
  }

  /**
   * Offline recovery on startup or connection restore (§10.2, SAAS-217 & SAAS-411).
   */
  public async checkAndRecoverDraft(): Promise<CIMDocument | null> {
    try {
      const draft = await this.draftStore.getDraft(this.documentId);
      if (draft && draft.document) {
        return draft.document;
      }
    } catch (err) {
      console.warn("Draft recovery check error:", err);
    }
    return null;
  }

  public destroy(): void {
    this.isDestroyed = true;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      if (this.boundOnlineHandler) {
        window.removeEventListener("online", this.boundOnlineHandler);
      }
      if (this.boundOfflineHandler) {
        window.removeEventListener("offline", this.boundOfflineHandler);
      }
    }
    this.listeners.clear();
  }
}

export interface SyncConflictUIOptions {
  onConflictResolved?: (result: {
    action: "fork" | "pull" | "download";
    branchId?: string;
    branchName?: string;
    revisionId?: string;
    doc?: CIMDocument;
    filename?: string;
  }) => void | Promise<void>;
  onNotice?: (msg: string, type?: "success" | "warning" | "error" | "info") => void;
}

/**
 * UI Component for displaying synchronization conflicts and recovery options.
 */
export class SyncConflictUI {
  private container: HTMLElement;
  private manager: DocumentSyncManager;
  private options: SyncConflictUIOptions;
  private bannerElement: HTMLElement | null = null;
  private isResolving = false;

  constructor(
    manager: DocumentSyncManager,
    container: HTMLElement = (typeof document !== "undefined" ? document.body : (null as any)),
    options: SyncConflictUIOptions = {}
  ) {
    this.manager = manager;
    this.container = container;
    this.options = options;

    this.manager.onStateChange((state, conflict) => {
      if (state === "conflict" && conflict) {
        this.show(conflict);
      } else if (state !== "conflict" && !this.isResolving) {
        // If not currently displaying success message
        if (!this.bannerElement || this.bannerElement.dataset.mode !== "success") {
          this.hide();
        }
      }
    });
  }

  public show(conflict: ConflictInfo): void {
    if (!this.bannerElement) {
      this.createBanner();
    }
    if (this.bannerElement) {
      this.bannerElement.dataset.mode = "conflict";
      this.bannerElement.style.display = "flex";
      this.bannerElement.style.background = "#fffbeb";
      this.bannerElement.style.border = "1px solid #f59e0b";

      const headerTitle = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-title");
      if (headerTitle) {
        headerTitle.textContent = "Kaydetme Çatışması (HTTP 409)";
        headerTitle.style.color = "#b45309";
      }

      const headerSub = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-sub");
      if (headerSub) {
        headerSub.textContent = "Verileriniz Korundu";
        headerSub.style.display = "inline";
      }

      const actionsDiv = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-actions");
      if (actionsDiv) {
        actionsDiv.style.display = "flex";
      }

      const closeBtn = this.bannerElement.querySelector<HTMLElement>("#p2i-conflict-close-btn");
      if (closeBtn) {
        closeBtn.style.display = "none";
      }

      const desc = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-desc");
      if (desc) {
        desc.textContent = `${conflict.message} Sunucudaki son revizyon: ${conflict.remoteHeadRevisionId?.substring(0, 8) || "Bilinmiyor"}.`;
        desc.style.color = "#78350f";
      }
    }
  }

  public showSuccess(message: string): void {
    if (!this.bannerElement) {
      this.createBanner();
    }
    if (this.bannerElement) {
      this.bannerElement.dataset.mode = "success";
      this.bannerElement.style.display = "flex";
      this.bannerElement.style.background = "#f0fdf4";
      this.bannerElement.style.border = "1px solid #22c55e";

      const headerTitle = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-title");
      if (headerTitle) {
        headerTitle.textContent = "Çatışma Çözüldü";
        headerTitle.style.color = "#15803d";
      }

      const headerSub = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-sub");
      if (headerSub) {
        headerSub.style.display = "none";
      }

      const desc = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-desc");
      if (desc) {
        desc.textContent = message;
        desc.style.color = "#166534";
      }

      const actionsDiv = this.bannerElement.querySelector<HTMLElement>(".p2i-conflict-actions");
      if (actionsDiv) {
        actionsDiv.style.display = "none";
      }

      let closeBtn = this.bannerElement.querySelector<HTMLButtonElement>("#p2i-conflict-close-btn");
      if (closeBtn) {
        closeBtn.style.display = "inline-block";
      }
    }
  }

  public hide(): void {
    if (this.bannerElement) {
      this.bannerElement.style.display = "none";
      this.bannerElement.dataset.mode = "hidden";
    }
  }

  public getBannerElement(): HTMLElement | null {
    return this.bannerElement;
  }

  private createBanner(): void {
    if (typeof document === "undefined") return;

    const banner = document.createElement("div");
    banner.id = "p2i-conflict-banner";
    banner.className = "p2i-conflict-banner";
    banner.setAttribute("role", "alert");
    banner.dataset.mode = "hidden";
    banner.style.cssText = `
      display: none;
      position: fixed;
      bottom: 20px;
      right: 20px;
      max-width: 480px;
      background: #fffbeb;
      border: 1px solid #f59e0b;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.15);
      z-index: 250;
      flex-direction: column;
      gap: 12px;
    `;

    banner.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <strong class="p2i-conflict-title" style="color: #b45309; font-size: 14px;">Kaydetme Çatışması (HTTP 409)</strong>
        <div style="display: flex; align-items: center; gap: 8px;">
          <span class="p2i-conflict-sub" style="font-size: 12px; color: #92400e;">Verileriniz Korundu</span>
          <button id="p2i-conflict-close-btn" class="p2i-btn p2i-btn-secondary" style="display: none; font-size: 12px; padding: 4px 8px;">Kapat</button>
        </div>
      </div>
      <p class="p2i-conflict-desc" style="margin: 0; font-size: 13px; color: #78350f; line-height: 1.4;"></p>
      <div class="p2i-conflict-actions" style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end;">
        <button id="p2i-conflict-download-btn" class="p2i-btn p2i-btn-secondary" style="font-size: 12px; padding: 6px 12px;">Taslağı İndir (JSON)</button>
        <button id="p2i-conflict-pull-btn" class="p2i-btn p2i-btn-warning" style="font-size: 12px; padding: 6px 12px;">Sunucudakini Al</button>
        <button id="p2i-conflict-fork-btn" class="p2i-btn p2i-btn-primary" style="font-size: 12px; padding: 6px 12px;">Çatışma Dalı Oluştur</button>
      </div>
    `;

    const forkBtn = banner.querySelector<HTMLButtonElement>("#p2i-conflict-fork-btn");
    const downloadBtn = banner.querySelector<HTMLButtonElement>("#p2i-conflict-download-btn");
    const pullBtn = banner.querySelector<HTMLButtonElement>("#p2i-conflict-pull-btn");
    const closeBtn = banner.querySelector<HTMLButtonElement>("#p2i-conflict-close-btn");

    closeBtn?.addEventListener("click", () => {
      this.hide();
    });

    forkBtn?.addEventListener("click", async () => {
      if (this.isResolving) return;
      this.isResolving = true;
      forkBtn.disabled = true;
      forkBtn.textContent = "Oluşturuluyor...";
      try {
        const res = await this.manager.forkConflictBranch();
        const successMsg = `Çatışma dalı oluşturuldu: ${res.branch.name}. Bu taslak ayrı olarak kaydedildi.`;
        this.showSuccess(successMsg);
        this.options.onNotice?.(successMsg, "success");
        await this.options.onConflictResolved?.({
          action: "fork",
          branchId: res.branchId,
          branchName: res.branch.name,
          revisionId: res.revisionId,
          doc: this.manager.getWorkflow().getActiveDocument(),
        });
      } catch (err: any) {
        alert(`Çatışma dalı oluşturma hatası: ${err.message}`);
        forkBtn.disabled = false;
        forkBtn.textContent = "Çatışma Dalı Oluştur";
      } finally {
        this.isResolving = false;
      }
    });

    downloadBtn?.addEventListener("click", () => {
      const dl = this.manager.downloadConflictDraft();
      const desc = banner.querySelector<HTMLElement>(".p2i-conflict-desc");
      if (desc) {
        desc.textContent = `Taslak indirildi (${dl.filename}). Çatışmayı çözmek için dal oluşturabilir veya sunucudaki halini alabilirsiniz.`;
      }
      this.options.onNotice?.(`Taslak indirildi: ${dl.filename}`, "info");
      this.options.onConflictResolved?.({
        action: "download",
        filename: dl.filename,
      });
    });

    pullBtn?.addEventListener("click", async () => {
      if (this.isResolving) return;
      const confirmed = typeof confirm === "function" ? confirm("Sunucudaki güncel hali çekmek istediğinize emin misiniz? Yerel düzenlemeleriniz silinecektir.") : true;
      if (confirmed) {
        this.isResolving = true;
        pullBtn.disabled = true;
        pullBtn.textContent = "Çekiliyor...";
        try {
          const serverDoc = await this.manager.discardLocalAndPull();
          const successMsg = "Sunucudaki güncel revizyon uygulandı.";
          this.showSuccess(successMsg);
          this.options.onNotice?.(successMsg, "success");
          await this.options.onConflictResolved?.({
            action: "pull",
            doc: serverDoc,
          });
        } catch (err: any) {
          alert(`Sunucudan çekme hatası: ${err.message}`);
          pullBtn.disabled = false;
          pullBtn.textContent = "Sunucudakini Al";
        } finally {
          this.isResolving = false;
        }
      }
    });

    if (this.container && typeof this.container.appendChild === "function") {
      this.container.appendChild(banner);
    }
    this.bannerElement = banner;
  }
}
